import { useEffect } from 'react';
import { engine } from '../hooks/useEngine.ts';
import { CloseIcon, DiceIcon } from './Icons.tsx';

interface Props {
  prompt: string | null;
  open: boolean;
  live: boolean;
}

/** How long the card stays up before folding away into the control bar. */
const AUTO_COLLAPSE_MS = 22_000;

/**
 * The shared conversation starter. Both people see the same text, which removes
 * the "so… hi?" moment that kills most first seconds.
 */
export function Icebreaker({ prompt, open, live }: Props) {
  useEffect(() => {
    if (!open || !prompt || !live) return;
    const id = window.setTimeout(() => engine.dismissPrompt(), AUTO_COLLAPSE_MS);
    return () => window.clearTimeout(id);
  }, [open, prompt, live]);

  if (!prompt || !open) return null;

  return (
    <div className="pointer-events-auto w-full max-w-md">
      <div className="glass relative overflow-hidden rounded-2xl px-4 py-3 pr-11 shadow-2xl ring-1 ring-phidio-500/25">
        <span
          className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-phidio-400 to-phidio-700"
          aria-hidden
        />
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-phidio-300">
          Ice-breaker
        </p>
        <p className="mt-1 text-sm leading-snug text-white/90 sm:text-base">{prompt}</p>

        <button
          type="button"
          onClick={() => engine.newPrompt()}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/8 px-3 py-1.5 text-xs font-medium text-white/70 transition hover:bg-white/16 hover:text-white"
        >
          <DiceIcon width={14} height={14} />
          Another one
        </button>

        <button
          type="button"
          onClick={() => engine.dismissPrompt()}
          aria-label="Hide the ice-breaker"
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-white/40 transition hover:bg-white/10 hover:text-white"
        >
          <CloseIcon width={15} height={15} />
        </button>
      </div>
    </div>
  );
}
